import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ShieldCheck,
  RotateCcw,
  Save,
  Loader2,
  Lock,
  CheckCircle2,
  AlertCircle,
  Users,
  Search,
  Check,
  X,
  Sliders,
  RefreshCw,
  UserCheck,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json',
});

const MODULES = [
  { key: 'dockets',   label: 'Dockets' },
  { key: 'trips',     label: 'Trips' },
  { key: 'fleet',     label: 'Fleet' },
  { key: 'customers', label: 'Customers' },
  { key: 'vendors',   label: 'Vendors' },
  { key: 'bookings',  label: 'Bookings' },
  { key: 'estimates', label: 'Estimates' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'tasks',     label: 'Tasks (TMS)' },
  { key: 'chat',      label: 'Team Chat' },
  { key: 'reports',   label: 'Reports' },
  { key: 'settings',  label: 'Settings' },
];

const ROLES = [
  { key: 'super_admin',     label: 'Super Admin',     color: 'bg-purple-100 text-purple-700 border border-purple-200', locked: true },
  { key: 'company_admin',   label: 'Company Admin',   color: 'bg-blue-100 text-blue-700 border border-blue-200',       locked: false },
  { key: 'manager',         label: 'Manager',         color: 'bg-green-100 text-green-700 border border-green-200',    locked: false },
  { key: 'driver',          label: 'Driver',          color: 'bg-orange-100 text-orange-700 border border-orange-200', locked: false },
  { key: 'accountant',      label: 'Accountant',      color: 'bg-cyan-100 text-cyan-700 border border-cyan-200',       locked: false },
  { key: 'warehouse_staff', label: 'Warehouse Staff', color: 'bg-red-100 text-red-700 border border-red-200',          locked: false },
];

const PERMS = ['can_view', 'can_create', 'can_edit', 'can_delete'];
const PERM_LABELS = { can_view: 'V', can_create: 'C', can_edit: 'E', can_delete: 'D' };
const PERM_FULL_NAMES = { can_view: 'View', can_create: 'Create', can_edit: 'Edit', can_delete: 'Delete' };

const emptyMatrix = () => {
  const m = {};
  for (const role of ROLES) {
    if (role.locked) continue;
    m[role.key] = {};
    for (const mod of MODULES) {
      m[role.key][mod.key] = { can_view: false, can_create: false, can_edit: false, can_delete: false };
    }
  }
  return m;
};

const RolePermissions = () => {
  // Navigation: 'roles' | 'users'
  const [activeTab, setActiveTab] = useState('roles');

  // Role Defaults State
  const [roleMatrix, setRoleMatrix] = useState(emptyMatrix());
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [savingRoles, setSavingRoles] = useState(false);
  const [resettingRoles, setResettingRoles] = useState(false);
  const [rolesDirty, setRolesDirty] = useState(false);

  // User Specific Permissions State
  const [usersList, setUsersList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [loadingUsersList, setLoadingUsersList] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [userMatrix, setUserMatrix] = useState({});
  const [userDirty, setUserDirty] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [resettingUser, setResettingUser] = useState(false);

  // -------------------------------------------------------------
  // Role Matrix Handlers
  // -------------------------------------------------------------
  const fetchRolePermissions = useCallback(async () => {
    setLoadingRoles(true);
    try {
      const res = await fetch(`${API}/api/settings/role-permissions`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to load role permissions');
      const data = await res.json();
      setRoleMatrix(prev => {
        const merged = { ...prev };
        for (const role of ROLES) {
          if (role.locked) continue;
          merged[role.key] = {};
          for (const mod of MODULES) {
            merged[role.key][mod.key] = data[role.key]?.[mod.key] || {
              can_view: false,
              can_create: false,
              can_edit: false,
              can_delete: false,
            };
          }
        }
        return merged;
      });
      setRolesDirty(false);
    } catch {
      toast.error('Failed to load role permissions');
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  useEffect(() => {
    fetchRolePermissions();
  }, [fetchRolePermissions]);

  const toggleRolePerm = (role, module, perm) => {
    setRoleMatrix(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [module]: {
          ...prev[role][module],
          [perm]: !prev[role][module][perm],
        },
      },
    }));
    setRolesDirty(true);
  };

  const saveRolePermissions = async () => {
    setSavingRoles(true);
    try {
      const res = await fetch(`${API}/api/settings/role-permissions`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(roleMatrix),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Role permissions saved successfully');
      setRolesDirty(false);
    } catch {
      toast.error('Failed to save role permissions');
    } finally {
      setSavingRoles(false);
    }
  };

  const resetRoleDefaults = async () => {
    if (!window.confirm('Reset all role permissions to standard system defaults?')) return;
    setResettingRoles(true);
    try {
      const res = await fetch(`${API}/api/settings/role-permissions/reset`, {
        method: 'POST',
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error();
      toast.success('Role permissions reset to defaults');
      await fetchRolePermissions();
    } catch {
      toast.error('Failed to reset role permissions');
    } finally {
      setResettingRoles(false);
    }
  };

  // -------------------------------------------------------------
  // User Permissions Handlers
  // -------------------------------------------------------------
  const fetchUsersList = useCallback(async () => {
    setLoadingUsersList(true);
    try {
      const res = await fetch(`${API}/api/settings/user-permissions/users`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to load user list');
      const data = await res.json();
      setUsersList(data);
      if (data.length > 0 && !selectedUserId) {
        setSelectedUserId(data[0].id);
      }
    } catch {
      toast.error('Failed to load users list');
    } finally {
      setLoadingUsersList(false);
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsersList();
    }
  }, [activeTab, fetchUsersList]);

  const fetchUserData = useCallback(async (userId) => {
    if (!userId) return;
    setLoadingUserData(true);
    try {
      const res = await fetch(`${API}/api/settings/user-permissions/${userId}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to load user permissions');
      const data = await res.json();
      setSelectedUserData(data);
      setIsCustomMode(data.has_custom);

      // Initialize user matrix with existing user permissions or role permissions
      const initialMatrix = {};
      const source = data.has_custom ? data.user_permissions : data.role_permissions;
      for (const mod of MODULES) {
        initialMatrix[mod.key] = {
          can_view: Boolean(source[mod.key]?.can_view),
          can_create: Boolean(source[mod.key]?.can_create),
          can_edit: Boolean(source[mod.key]?.can_edit),
          can_delete: Boolean(source[mod.key]?.can_delete),
        };
      }
      setUserMatrix(initialMatrix);
      setUserDirty(false);
    } catch {
      toast.error('Failed to load user permissions');
    } finally {
      setLoadingUserData(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'users' && selectedUserId) {
      fetchUserData(selectedUserId);
    }
  }, [activeTab, selectedUserId, fetchUserData]);

  const toggleUserPerm = (module, perm) => {
    if (!isCustomMode) return;
    setUserMatrix(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [perm]: !prev[module]?.[perm],
      },
    }));
    setUserDirty(true);
  };

  const setRowAll = (module, value) => {
    if (!isCustomMode) return;
    setUserMatrix(prev => ({
      ...prev,
      [module]: {
        can_view: value,
        can_create: value,
        can_edit: value,
        can_delete: value,
      },
    }));
    setUserDirty(true);
  };

  const handleModeSwitch = (newCustomMode) => {
    if (newCustomMode === isCustomMode) return;
    if (!newCustomMode) {
      // Switching from Custom to Inherit
      if (userDirty && !window.confirm('Discard unsaved custom changes and switch to Role Inheritance?')) {
        return;
      }
      setIsCustomMode(false);
      setUserDirty(selectedUserData?.has_custom !== false);
    } else {
      // Switching from Inherit to Custom
      setIsCustomMode(true);
      // Pre-fill user matrix from role permissions
      const initialMatrix = {};
      const source = selectedUserData?.role_permissions || {};
      for (const mod of MODULES) {
        initialMatrix[mod.key] = {
          can_view: Boolean(source[mod.key]?.can_view),
          can_create: Boolean(source[mod.key]?.can_create),
          can_edit: Boolean(source[mod.key]?.can_edit),
          can_delete: Boolean(source[mod.key]?.can_delete),
        };
      }
      setUserMatrix(initialMatrix);
      setUserDirty(true);
    }
  };

  const saveUserPermissions = async () => {
    if (!selectedUserId) return;
    setSavingUser(true);
    try {
      const payload = {
        has_custom: isCustomMode,
        permissions: isCustomMode ? userMatrix : {},
      };
      const res = await fetch(`${API}/api/settings/user-permissions/${selectedUserId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success(isCustomMode ? 'Custom user permissions saved' : 'User reverted to role inheritance');
      setUserDirty(false);
      await fetchUserData(selectedUserId);
      await fetchUsersList();
    } catch {
      toast.error('Failed to save user permissions');
    } finally {
      setSavingUser(false);
    }
  };

  const resetUserToDefaults = async () => {
    if (!selectedUserId) return;
    if (!window.confirm('Revert this user back to default role permissions? All custom overrides will be removed.')) return;
    setResettingUser(true);
    try {
      const res = await fetch(`${API}/api/settings/user-permissions/${selectedUserId}/reset`, {
        method: 'POST',
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error();
      toast.success('User reverted to role defaults');
      setUserDirty(false);
      await fetchUserData(selectedUserId);
      await fetchUsersList();
    } catch {
      toast.error('Failed to reset user permissions');
    } finally {
      setResettingUser(false);
    }
  };

  const copyFromRoleDefaults = () => {
    if (!selectedUserData?.role_permissions) return;
    const cloned = {};
    for (const mod of MODULES) {
      cloned[mod.key] = {
        can_view: Boolean(selectedUserData.role_permissions[mod.key]?.can_view),
        can_create: Boolean(selectedUserData.role_permissions[mod.key]?.can_create),
        can_edit: Boolean(selectedUserData.role_permissions[mod.key]?.can_edit),
        can_delete: Boolean(selectedUserData.role_permissions[mod.key]?.can_delete),
      };
    }
    setUserMatrix(cloned);
    setUserDirty(true);
    toast.info('Copied role baseline permissions into editor');
  };

  // Filtered users for dropdown search
  const filteredUsers = usersList.filter(u => {
    const q = userSearchTerm.toLowerCase();
    return (
      (u.full_name && u.full_name.toLowerCase().includes(q)) ||
      (u.email && u.email.toLowerCase().includes(q)) ||
      (u.role && u.role.toLowerCase().includes(q))
    );
  });

  const currentUserObj = usersList.find(u => u.id === selectedUserId);
  const currentRoleConfig = ROLES.find(r => r.key === (selectedUserData?.user?.role || currentUserObj?.role));

  return (
    <div className="space-y-6" data-testid="role-permissions-page">
      {/* Top Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent-blue/10 flex items-center justify-center text-accent-blue mt-0.5 flex-shrink-0">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-text-dark font-manrope">Access & Permissions Matrix</h2>
            <p className="text-sm text-text-secondary font-ibm mt-0.5">
              Manage baseline role templates or apply granular per-user overrides across all system modules.
            </p>
          </div>
        </div>

        {/* Tab Switcher Pills */}
        <div className="inline-flex p-1 bg-gray-100 rounded-xl border border-gray-200">
          <button
            onClick={() => setActiveTab('roles')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold font-ibm transition-all ${
              activeTab === 'roles'
                ? 'bg-white text-accent-blue shadow-sm'
                : 'text-text-secondary hover:text-text-dark'
            }`}
            data-testid="tab-roles"
          >
            <ShieldCheck className="w-4 h-4" />
            Role Baseline Defaults
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold font-ibm transition-all ${
              activeTab === 'users'
                ? 'bg-white text-accent-blue shadow-sm'
                : 'text-text-secondary hover:text-text-dark'
            }`}
            data-testid="tab-users"
          >
            <Users className="w-4 h-4" />
            User-Specific Permissions
          </button>
        </div>
      </div>

      {/* ============================================================== */}
      {/* TAB 1: ROLE BASELINE DEFAULTS                                   */}
      {/* ============================================================== */}
      {activeTab === 'roles' && (
        <div className="space-y-5 animate-in fade-in duration-200">
          {/* Action Toolbar & Legend */}
          <div className="flex items-center justify-between gap-4 flex-wrap bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-5 flex-wrap text-xs font-ibm text-text-secondary">
              <span className="font-semibold text-text-dark uppercase tracking-wider text-[11px]">Legend:</span>
              {PERMS.map(p => (
                <span key={p} className="flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded bg-gray-100 border border-gray-300 inline-flex items-center justify-center font-bold text-text-dark text-[11px]">
                    {PERM_LABELS[p]}
                  </span>
                  <span>{PERM_FULL_NAMES[p]}</span>
                </span>
              ))}
              <span className="flex items-center gap-1.5 text-purple-700 bg-purple-50 px-2.5 py-1 rounded-md font-medium border border-purple-200">
                <Lock className="w-3.5 h-3.5" /> Super Admin — Full system access (locked)
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={resetRoleDefaults}
                disabled={resettingRoles || loadingRoles}
                className="font-ibm text-text-secondary hover:text-text-dark"
                data-testid="reset-defaults-btn"
              >
                {resettingRoles ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
                Reset Defaults
              </Button>
              <Button
                size="sm"
                onClick={saveRolePermissions}
                disabled={savingRoles || !rolesDirty || loadingRoles}
                className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm"
                data-testid="save-permissions-btn"
              >
                {savingRoles ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save Changes
              </Button>
            </div>
          </div>

          {rolesDirty && (
            <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 font-ibm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              You have unsaved changes in the role baseline matrix. Click "Save Changes" to apply.
            </div>
          )}

          {/* Role Matrix Table */}
          {loadingRoles ? (
            <div className="flex items-center justify-center py-20 bg-white rounded-xl border border-gray-100">
              <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
            </div>
          ) : (
            <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
              <table className="w-full min-w-[980px]">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-6 py-4 text-xs font-semibold text-text-dark font-ibm uppercase tracking-wider w-40">
                      Module
                    </th>
                    {ROLES.map(role => (
                      <th key={role.key} className="px-3 py-4 text-center" colSpan={4}>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold font-ibm ${role.color}`}>
                          {role.locked && <Lock className="w-3 h-3" />}
                          {role.label}
                        </span>
                        <div className="flex justify-center gap-2.5 mt-2">
                          {PERMS.map(p => (
                            <span key={p} className="w-7 text-center text-[11px] font-bold text-text-secondary font-ibm">
                              {PERM_LABELS[p]}
                            </span>
                          ))}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {MODULES.map((mod, mIdx) => (
                    <tr
                      key={mod.key}
                      className={`hover:bg-blue-50/20 transition-colors ${mIdx % 2 === 0 ? '' : 'bg-gray-50/25'}`}
                      data-testid={`perm-row-${mod.key}`}
                    >
                      <td className="px-6 py-3.5">
                        <span className="text-sm font-semibold text-text-dark font-ibm">{mod.label}</span>
                      </td>
                      {ROLES.map(role => (
                        <td key={role.key} className="px-3 py-3.5" colSpan={4}>
                          <div className="flex justify-center gap-2.5">
                            {PERMS.map(perm => {
                              const isLocked = role.locked;
                              const checked = isLocked || (roleMatrix[role.key]?.[mod.key]?.[perm] ?? false);
                              return (
                                <button
                                  key={perm}
                                  data-testid={`perm-${role.key}-${mod.key}-${perm}`}
                                  onClick={() => !isLocked && toggleRolePerm(role.key, mod.key, perm)}
                                  disabled={isLocked}
                                  className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${
                                    isLocked
                                      ? 'bg-purple-100 border border-purple-200 cursor-not-allowed'
                                      : checked
                                      ? 'bg-green-600 border border-green-700 hover:bg-green-700 cursor-pointer shadow-xs'
                                      : 'bg-gray-100 border border-gray-200 hover:bg-gray-200 cursor-pointer'
                                  }`}
                                  title={`${role.label} — ${mod.label} — ${PERM_FULL_NAMES[perm]}`}
                                >
                                  {checked && (
                                    <CheckCircle2 className={`w-4 h-4 ${isLocked ? 'text-purple-500' : 'text-white'}`} />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          <p className="text-xs text-text-secondary font-ibm px-1">
            Note: Changes made here define the default baseline for all users assigned to each role, unless a user has explicit custom permissions configured.
          </p>
        </div>
      )}

      {/* ============================================================== */}
      {/* TAB 2: USER-SPECIFIC PERMISSIONS                                */}
      {/* ============================================================== */}
      {activeTab === 'users' && (
        <div className="space-y-5 animate-in fade-in duration-200">
          {/* User Selection & Configuration Bar */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* User Selector Column */}
            <Card className="lg:col-span-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-text-dark uppercase tracking-wider font-ibm">
                  Select User ({usersList.length})
                </span>
                {loadingUsersList && <Loader2 className="w-3.5 h-3.5 animate-spin text-accent-blue" />}
              </div>

              {/* Search input */}
              <div className="relative">
                <Search className="w-4 h-4 text-text-secondary absolute left-3 top-2.5" />
                <input
                  type="text"
                  placeholder="Filter users by name or role..."
                  value={userSearchTerm}
                  onChange={e => setUserSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs font-ibm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent-blue"
                />
              </div>

              {/* Users list scroll container */}
              <div className="max-h-72 overflow-y-auto space-y-1 pr-1 divide-y divide-gray-50">
                {filteredUsers.length === 0 ? (
                  <div className="py-6 text-center text-xs text-text-secondary font-ibm">
                    No users match your search.
                  </div>
                ) : (
                  filteredUsers.map(user => {
                    const isSelected = user.id === selectedUserId;
                    const rConfig = ROLES.find(r => r.key === user.role);
                    return (
                      <button
                        key={user.id}
                        onClick={() => setSelectedUserId(user.id)}
                        className={`w-full text-left p-2.5 rounded-lg transition-all flex items-start justify-between gap-2 ${
                          isSelected
                            ? 'bg-blue-50/80 border border-blue-200 shadow-xs'
                            : 'hover:bg-gray-50 border border-transparent'
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-text-dark font-ibm truncate">
                            {user.full_name}
                          </div>
                          <div className="text-[11px] text-text-secondary font-ibm truncate">
                            {user.email}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${rConfig?.color || 'bg-gray-100 text-gray-700'}`}>
                            {rConfig?.label || user.role}
                          </span>
                          {user.has_custom ? (
                            <span className="text-[9px] font-bold text-amber-700 bg-amber-100/80 px-1.5 py-0.2 rounded">
                              Custom Overrides
                            </span>
                          ) : (
                            <span className="text-[9px] text-gray-500 bg-gray-100 px-1.5 py-0.2 rounded">
                              Role Default
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </Card>

            {/* Selected User Overview & Mode Switcher */}
            <Card className="lg:col-span-8 bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between gap-4">
              {selectedUserData?.user ? (
                <>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-blue/10 to-accent-blue/20 flex items-center justify-center text-accent-blue font-bold font-manrope text-lg">
                        {selectedUserData.user.full_name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-bold text-text-dark font-manrope">
                            {selectedUserData.user.full_name}
                          </h3>
                          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${currentRoleConfig?.color || 'bg-gray-100 text-gray-700'}`}>
                            {currentRoleConfig?.label || selectedUserData.user.role}
                          </span>
                        </div>
                        <p className="text-xs text-text-secondary font-ibm mt-0.5">
                          {selectedUserData.user.email}
                        </p>
                      </div>
                    </div>

                    {/* Mode Status Pill */}
                    <div>
                      {isCustomMode ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 bg-amber-100 border border-amber-300 px-3 py-1 rounded-full">
                          <Sliders className="w-3.5 h-3.5 text-amber-700" /> Custom User Permissions Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-800 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full">
                          <UserCheck className="w-3.5 h-3.5 text-blue-600" /> Inheriting from Role Defaults
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Mode Selector Toggle Box */}
                  <div className="bg-gray-50/80 rounded-xl p-3 border border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleModeSwitch(false)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold font-ibm transition-all flex items-center gap-1.5 ${
                          !isCustomMode
                            ? 'bg-white text-blue-700 shadow-xs border border-blue-200'
                            : 'text-text-secondary hover:text-text-dark'
                        }`}
                      >
                        <UserCheck className="w-3.5 h-3.5" />
                        Inherit from {currentRoleConfig?.label || 'Role'}
                      </button>
                      <button
                        onClick={() => handleModeSwitch(true)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold font-ibm transition-all flex items-center gap-1.5 ${
                          isCustomMode
                            ? 'bg-white text-amber-800 shadow-xs border border-amber-300'
                            : 'text-text-secondary hover:text-text-dark'
                        }`}
                      >
                        <Sliders className="w-3.5 h-3.5" />
                        Custom User Permissions
                      </button>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                      {isCustomMode && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={copyFromRoleDefaults}
                            className="text-xs font-ibm text-text-secondary h-8"
                            title="Reset matrix to match role defaults without reverting mode"
                          >
                            <RefreshCw className="w-3.5 h-3.5 mr-1" />
                            Copy Role Baseline
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={resetUserToDefaults}
                            disabled={resettingUser}
                            className="text-xs font-ibm text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 h-8"
                            title="Revert back to role default inheritance"
                          >
                            {resettingUser ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
                            Revert to Role
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        onClick={saveUserPermissions}
                        disabled={savingUser || !userDirty}
                        className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm text-xs h-8"
                      >
                        {savingUser ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                        Save Permissions
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="py-8 text-center text-text-secondary font-ibm text-sm">
                  Select a user on the left to configure permissions.
                </div>
              )}
            </Card>
          </div>

          {userDirty && (
            <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 font-ibm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Unsaved changes for this user. Click "Save Permissions" to commit.
            </div>
          )}

          {/* User Permission Table */}
          {loadingUserData ? (
            <div className="flex items-center justify-center py-20 bg-white rounded-xl border border-gray-100">
              <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
            </div>
          ) : selectedUserData ? (
            <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              {!isCustomMode && (
                <div className="bg-blue-50/60 border-b border-blue-100 px-5 py-3 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-xs font-ibm text-blue-900">
                    <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>
                      <strong>Read-Only Preview:</strong> This user currently inherits the standard baseline permissions of their role (<strong>{currentRoleConfig?.label}</strong>).
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleModeSwitch(true)}
                    className="text-xs font-ibm text-blue-700 bg-white border-blue-300 hover:bg-blue-50 h-7"
                  >
                    <Sliders className="w-3 h-3 mr-1" />
                    Customize for this User
                  </Button>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/60">
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-text-dark font-ibm uppercase tracking-wider w-48">
                        Module
                      </th>
                      {PERMS.map(p => (
                        <th key={p} className="px-4 py-3.5 text-center text-xs font-semibold text-text-dark font-ibm uppercase tracking-wider w-24">
                          <div className="flex flex-col items-center">
                            <span>{PERM_FULL_NAMES[p]}</span>
                            <span className="text-[10px] text-text-secondary font-normal font-ibm">({PERM_LABELS[p]})</span>
                          </div>
                        </th>
                      ))}
                      {isCustomMode && (
                        <th className="px-6 py-3.5 text-right text-xs font-semibold text-text-dark font-ibm uppercase tracking-wider w-36">
                          Quick Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {MODULES.map((mod, mIdx) => {
                      const perms = isCustomMode
                        ? (userMatrix[mod.key] || {})
                        : (selectedUserData.role_permissions?.[mod.key] || {});

                      const allChecked = PERMS.every(p => perms[p]);

                      return (
                        <tr
                          key={mod.key}
                          className={`hover:bg-blue-50/20 transition-colors ${mIdx % 2 === 0 ? '' : 'bg-gray-50/20'}`}
                        >
                          <td className="px-6 py-3.5">
                            <span className="text-sm font-semibold text-text-dark font-ibm">{mod.label}</span>
                          </td>

                          {PERMS.map(p => {
                            const checked = Boolean(perms[p]);
                            return (
                              <td key={p} className="px-4 py-3.5 text-center">
                                <button
                                  type="button"
                                  onClick={() => toggleUserPerm(mod.key, p)}
                                  disabled={!isCustomMode}
                                  className={`w-7 h-7 mx-auto rounded-md flex items-center justify-center transition-all ${
                                    !isCustomMode
                                      ? checked
                                        ? 'bg-blue-100 border border-blue-200 cursor-not-allowed text-blue-700'
                                        : 'bg-gray-100 border border-gray-200 cursor-not-allowed text-gray-300'
                                      : checked
                                      ? 'bg-green-600 border border-green-700 hover:bg-green-700 text-white cursor-pointer shadow-xs'
                                      : 'bg-gray-100 border border-gray-200 hover:bg-gray-200 text-transparent cursor-pointer'
                                  }`}
                                  title={`${mod.label} — ${PERM_FULL_NAMES[p]}`}
                                >
                                  {checked && <Check className="w-4 h-4 stroke-[3]" />}
                                </button>
                              </td>
                            );
                          })}

                          {isCustomMode && (
                            <td className="px-6 py-3.5 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => setRowAll(mod.key, !allChecked)}
                                  className="text-[11px] font-ibm font-medium text-text-secondary hover:text-accent-blue px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                                >
                                  {allChecked ? 'Clear' : 'Select All'}
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default RolePermissions;
